const GOOGLE_ADS_ID = process.env.REACT_APP_GOOGLE_ADS_ID;
const DEFAULT_CONVERSION_LABEL = process.env.REACT_APP_GOOGLE_ADS_CONVERSION_LABEL;
const SIGNUP_INTENT_LABEL = process.env.REACT_APP_GOOGLE_ADS_SIGNUP_INTENT_LABEL;
const ONBOARDING_COMPLETE_LABEL = process.env.REACT_APP_GOOGLE_ADS_ONBOARDING_COMPLETE_LABEL;
const WORK_EMAIL_SIGNUP_LABEL = process.env.REACT_APP_GOOGLE_ADS_WORK_EMAIL_SIGNUP_LABEL;
const COMPANY_DOMAIN_SUBMITTED_LABEL = process.env.REACT_APP_GOOGLE_ADS_COMPANY_DOMAIN_SUBMITTED_LABEL;
const COMPANY_VERIFICATION_COMPLETED_LABEL = process.env.REACT_APP_GOOGLE_ADS_COMPANY_VERIFICATION_COMPLETED_LABEL;
const FIRST_SCAN_COMPLETED_LABEL = process.env.REACT_APP_GOOGLE_ADS_FIRST_SCAN_COMPLETED_LABEL;
const PROFILE_PUBLISHED_LABEL = process.env.REACT_APP_GOOGLE_ADS_PROFILE_PUBLISHED_LABEL;
const BADGE_ENABLED_LABEL = process.env.REACT_APP_GOOGLE_ADS_BADGE_ENABLED_LABEL;
const PRICING_INTELLIGENCE_VIEWED_LABEL = process.env.REACT_APP_GOOGLE_ADS_PRICING_INTELLIGENCE_VIEWED_LABEL;
const CONTACT_SALES_LABEL = process.env.REACT_APP_GOOGLE_ADS_CONTACT_SALES_LABEL;
const CONSENT_STORAGE_KEY = 'risqly_cookie_consent';
const CONVERSION_STORAGE_PREFIX = 'risqly_google_ads_conversion_';
const CONSENT_ACCEPTED = 'accepted';
const CONSENT_REJECTED = 'rejected';

const consentFields = {
  ad_storage: 'denied',
  analytics_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
};

const safeLocalStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage;
  } catch (err) {
    return null;
  }
};

const ensureGtag = () => {
  if (typeof window === 'undefined') {
    return false;
  }

  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function gtag() {
    window.dataLayer.push(arguments);
  };

  if (!window.__risqlyGoogleConsentDefaultSet) {
    window.gtag('consent', 'default', {
      ...consentFields,
      wait_for_update: 500,
    });
    window.__risqlyGoogleConsentDefaultSet = true;
  }

  return true;
};

export const getCookieConsentChoice = () => {
  const storage = safeLocalStorage();

  if (!storage) {
    return null;
  }

  const choice = storage.getItem(CONSENT_STORAGE_KEY);
  return choice === CONSENT_ACCEPTED || choice === CONSENT_REJECTED ? choice : null;
};

export const hasGoogleAdsConsent = () => getCookieConsentChoice() === CONSENT_ACCEPTED;

export const updateGoogleAdsConsent = (choice) => {
  const normalizedChoice = choice === CONSENT_ACCEPTED ? CONSENT_ACCEPTED : CONSENT_REJECTED;
  const storage = safeLocalStorage();

  if (storage) {
    storage.setItem(CONSENT_STORAGE_KEY, normalizedChoice);
  }

  if (!ensureGtag()) {
    return false;
  }

  const granted = normalizedChoice === CONSENT_ACCEPTED;

  window.gtag('consent', 'update', {
    ad_storage: granted ? 'granted' : 'denied',
    analytics_storage: granted ? 'granted' : 'denied',
    ad_user_data: granted ? 'granted' : 'denied',
    ad_personalization: granted ? 'granted' : 'denied',
  });

  if (granted) {
    initGoogleAds();
  }

  return granted;
};

const getConversionLabel = (labelName) => {
  const labels = {
    signup_intent: SIGNUP_INTENT_LABEL,
    onboarding_complete: ONBOARDING_COMPLETE_LABEL,
    work_email_signup: WORK_EMAIL_SIGNUP_LABEL,
    company_domain_submitted: COMPANY_DOMAIN_SUBMITTED_LABEL,
    company_verification_completed: COMPANY_VERIFICATION_COMPLETED_LABEL,
    first_scan_completed: FIRST_SCAN_COMPLETED_LABEL,
    profile_published: PROFILE_PUBLISHED_LABEL,
    badge_enabled: BADGE_ENABLED_LABEL,
    pricing_intelligence_viewed: PRICING_INTELLIGENCE_VIEWED_LABEL,
    contact_sales: CONTACT_SALES_LABEL,
  };

  return labels[labelName] || DEFAULT_CONVERSION_LABEL;
};

const getConversionStorageKey = (labelName, onceKey) => {
  const normalizedKey = onceKey || labelName;
  return `${CONVERSION_STORAGE_PREFIX}${normalizedKey}`;
};

export const initGoogleAds = () => {
  if (!GOOGLE_ADS_ID || typeof window === 'undefined' || typeof document === 'undefined') {
    return false;
  }

  ensureGtag();

  if (!hasGoogleAdsConsent()) {
    return false;
  }

  window.gtag('consent', 'update', {
    ad_storage: 'granted',
    analytics_storage: 'granted',
    ad_user_data: 'granted',
    ad_personalization: 'granted',
  });

  if (!document.querySelector(`script[src="https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}"]`)) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GOOGLE_ADS_ID}`;
    document.head.appendChild(script);
  }

  if (!window.__risqlyGoogleAdsInitialized) {
    window.gtag('js', new Date());
    window.gtag('config', GOOGLE_ADS_ID);
    window.__risqlyGoogleAdsInitialized = true;
  }

  return true;
};

export const trackGoogleAdsConversion = (labelName, options = {}) => {
  const conversionLabel = getConversionLabel(labelName);
  const storage = safeLocalStorage();
  const conversionStorageKey = getConversionStorageKey(labelName, options.onceKey);

  if (options.once && storage?.getItem(conversionStorageKey) === 'tracked') {
    return false;
  }

  if (!GOOGLE_ADS_ID || !conversionLabel || !hasGoogleAdsConsent() || typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return false;
  }

  window.gtag('event', 'conversion', {
    send_to: `${GOOGLE_ADS_ID}/${conversionLabel}`,
    value: options.value,
    currency: options.currency,
    event_callback: options.eventCallback,
  });

  if (options.once && storage) {
    storage.setItem(conversionStorageKey, 'tracked');
  }

  return true;
};
